import React, { useEffect, useMemo, useState } from "react";
import PortalLayout from "@/components/PortalLayout";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, LayoutList, Columns3, Trash2, CalendarDays } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const STATUS = [
  { value: "todo", label: "De făcut", cls: "pio-badge-gray" },
  { value: "in_progress", label: "În lucru", cls: "pio-badge-blue" },
  { value: "done", label: "Finalizat", cls: "pio-badge-green" },
];
const PRIORITY = [
  { value: "low", label: "Scăzută", dot: "#94a3b8" },
  { value: "normal", label: "Normală", dot: "#3d68b1" },
  { value: "high", label: "Ridicată", dot: "#dc2626" },
];

const stMeta = (v) => STATUS.find(s => s.value === v) || STATUS[0];
const prMeta = (v) => PRIORITY.find(p => p.value === v) || PRIORITY[1];

function fmtDue(iso) {
  if (!iso) return null;
  const d = new Date(iso + "T00:00:00");
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const overdue = d < today;
  const m = ["ian", "feb", "mar", "apr", "mai", "iun", "iul", "aug", "sep", "oct", "nov", "dec"];
  return { text: `${d.getDate()} ${m[d.getMonth()]} ${d.getFullYear() !== today.getFullYear() ? d.getFullYear() : ""}`.trim(), overdue };
}

function Initials({ name }) {
  if (!name) return <span className="text-[11px] text-slate-300">—</span>;
  const ini = name.split(" ").slice(0, 2).map(x => x[0]).join("").toUpperCase();
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-5 w-5 rounded-full bg-brand/10 text-brand text-[9px] font-bold flex items-center justify-center shrink-0">{ini}</span>
      <span className="text-[12px] text-slate-600 truncate max-w-[130px]">{name}</span>
    </span>
  );
}

const EMPTY_FORM = { title: "", description: "", project_id: "", assignee_id: "", due_date: "", priority: "normal", status: "todo" };

export default function Tasks() {
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [ctx, setCtx] = useState({ projects: [], employees: [], can_manage: false });
  const [view, setView] = useState("list");
  const [fProject, setFProject] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [panel, setPanel] = useState(null); // {mode:'new'|'edit', task?}
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [tR, cR] = await Promise.all([api.get("/pm/tasks"), api.get("/pm/context")]);
      setTasks(tR.data); setCtx(cR.data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => tasks.filter(t =>
    (!fProject || t.project_id === fProject) && (!fStatus || t.status === fStatus)
  ), [tasks, fProject, fStatus]);

  const openNew = () => { setForm({ ...EMPTY_FORM, project_id: ctx.projects[0]?.id || "" }); setPanel({ mode: "new" }); };
  const openEdit = (t) => {
    setForm({ title: t.title, description: t.description || "", project_id: t.project_id,
      assignee_id: t.assignee_id || "", due_date: t.due_date || "", priority: t.priority, status: t.status });
    setPanel({ mode: "edit", task: t });
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Titlul este obligatoriu"); return; }
    if (!form.project_id) { toast.error("Selectează un proiect"); return; }
    setSaving(true);
    try {
      if (panel.mode === "new") {
        await api.post("/pm/tasks", { ...form, assignee_id: form.assignee_id || null, due_date: form.due_date || null });
        toast.success("Sarcină creată");
      } else {
        await api.put(`/pm/tasks/${panel.task.id}`, form);
        toast.success("Sarcină actualizată");
      }
      setPanel(null); await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Ștergi această sarcină?")) return;
    try { await api.delete(`/pm/tasks/${panel.task.id}`); toast.success("Sarcină ștearsă"); setPanel(null); await load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const quickStatus = async (t, status) => {
    try {
      await api.put(`/pm/tasks/${t.id}`, { status });
      setTasks(prev => prev.map(x => x.id === t.id ? { ...x, status } : x));
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <PortalLayout variant="staff">
      <div data-testid="tasks-page">
        <div className="flex items-end justify-between mb-4 gap-3">
          <div>
            <div className="section-eyebrow">Proiecte</div>
            <h1 className="section-title">Sarcini</h1>
          </div>
          <button data-testid="task-new-btn" onClick={openNew} className="pio-btn shrink-0"><Plus /> Sarcină nouă</button>
        </div>

        <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-2">
          <div className="flex flex-col md:flex-row md:items-center gap-2 min-w-0">
            <select data-testid="task-filter-project" value={fProject} onChange={e => setFProject(e.target.value)} className="pio-input w-full md:max-w-[240px]">
              <option value="">Toate proiectele</option>
              {ctx.projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <div className="pio-tabs !border-0 overflow-x-auto">
              <button data-testid="task-filter-all" className={`pio-tab !border-b-2 ${fStatus === "" ? "is-active" : ""}`} onClick={() => setFStatus("")}>Toate</button>
              {STATUS.map(s => (
                <button key={s.value} data-testid={`task-filter-${s.value}`}
                  className={`pio-tab !border-b-2 ${fStatus === s.value ? "is-active" : ""}`}
                  onClick={() => setFStatus(s.value)}>{s.label}</button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button data-testid="task-view-list" onClick={() => setView("list")}
              className={`pio-btn-ghost !px-2 ${view === "list" ? "!bg-slate-100 !border-slate-300" : ""}`}><LayoutList /></button>
            <button data-testid="task-view-board" onClick={() => setView("board")}
              className={`pio-btn-ghost !px-2 ${view === "board" ? "!bg-slate-100 !border-slate-300" : ""}`}><Columns3 /></button>
          </div>
        </div>

        {loading ? (
          <div className="py-24 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : view === "list" ? (
          <>
          <div className="md:hidden" data-testid="tasks-cards">
            {filtered.length === 0 && (
              <div className="py-12 text-center text-slate-400 text-sm">Nicio sarcină. Creează prima sarcină cu butonul de sus.</div>
            )}
            {filtered.map(t => {
              const due = fmtDue(t.due_date);
              const st = stMeta(t.status); const pr = prMeta(t.priority);
              return (
                <div key={t.id} data-testid={`task-card-${t.id}`} className="m-card" onClick={() => openEdit(t)}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className={`font-semibold text-[14.5px] ${t.status === "done" ? "line-through text-slate-400" : "text-slate-800"}`}>{t.title}</div>
                      {t.description && <div className="text-[12px] text-slate-400 line-clamp-2 mt-0.5">{t.description}</div>}
                    </div>
                    <Initials name={t.assignee_name} />
                  </div>
                  <div className="flex items-center gap-2 flex-wrap mt-3 text-[12px] text-slate-600">
                    <span className="pio-badge pio-badge-gray">{t.project_code || t.project_name}</span>
                    <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: pr.dot }} />{pr.label}</span>
                    {due && (
                      <span className={`inline-flex items-center gap-1 tabular-nums ${due.overdue && t.status !== "done" ? "text-red-600 font-semibold" : "text-slate-500"}`}>
                        <CalendarDays className="h-3 w-3" />{due.text}
                      </span>
                    )}
                    <select value={t.status} onChange={e => quickStatus(t, e.target.value)} onClick={e => e.stopPropagation()}
                      data-testid={`task-status-select-m-${t.id}`}
                      className={`pio-badge ${st.cls} !h-7 border-0 cursor-pointer appearance-none pr-2 ml-auto !text-[12px]`}>
                      {STATUS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="surface overflow-hidden hidden md:block">
            <table className="pio-table">
              <thead>
                <tr>
                  <th className="w-[34%]">Sarcină</th>
                  <th>Proiect</th>
                  <th>Responsabil</th>
                  <th>Termen</th>
                  <th>Prioritate</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={6} className="py-12 text-center text-slate-400">Nicio sarcină. Creează prima sarcină cu butonul de sus.</td></tr>
                )}
                {filtered.map(t => {
                  const due = fmtDue(t.due_date);
                  const st = stMeta(t.status); const pr = prMeta(t.priority);
                  return (
                    <tr key={t.id} data-testid={`task-row-${t.id}`} className="cursor-pointer" onClick={() => openEdit(t)}>
                      <td>
                        <div className={`font-medium text-[13px] ${t.status === "done" ? "line-through text-slate-400" : "text-slate-800"}`}>{t.title}</div>
                        {t.description && <div className="text-[11px] text-slate-400 truncate max-w-[360px]">{t.description}</div>}
                      </td>
                      <td><span className="text-[12px] text-slate-600">{t.project_code || t.project_name}</span></td>
                      <td><Initials name={t.assignee_name} /></td>
                      <td>
                        {due ? (
                          <span className={`inline-flex items-center gap-1 text-[12px] tabular-nums ${due.overdue && t.status !== "done" ? "text-red-600 font-semibold" : "text-slate-500"}`}>
                            <CalendarDays className="h-3 w-3" />{due.text}
                          </span>
                        ) : <span className="text-slate-300 text-[12px]">—</span>}
                      </td>
                      <td>
                        <span className="inline-flex items-center gap-1.5 text-[12px] text-slate-600">
                          <span className="h-2 w-2 rounded-full" style={{ background: pr.dot }} />{pr.label}
                        </span>
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <select value={t.status} onChange={e => quickStatus(t, e.target.value)}
                          data-testid={`task-status-select-${t.id}`}
                          className={`pio-badge ${st.cls} !h-6 border-0 cursor-pointer appearance-none pr-2`}>
                          {STATUS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {STATUS.map(col => {
              const list = filtered.filter(t => t.status === col.value);
              return (
                <div key={col.value} className="surface-inset p-3 min-h-[300px]" data-testid={`board-col-${col.value}`}>
                  <div className="flex items-center justify-between mb-3 px-1">
                    <span className={`pio-badge ${col.cls}`}>{col.label}</span>
                    <span className="text-[11px] text-slate-400 tabular-nums">{list.length}</span>
                  </div>
                  <div className="space-y-2">
                    {list.map(t => {
                      const due = fmtDue(t.due_date); const pr = prMeta(t.priority);
                      return (
                        <div key={t.id} onClick={() => openEdit(t)} data-testid={`board-card-${t.id}`}
                          className="surface p-3 cursor-pointer hover:border-slate-300 transition-colors">
                          <div className="flex items-start gap-2 mb-1.5">
                            <span className="h-2 w-2 rounded-full mt-1.5 shrink-0" style={{ background: pr.dot }} />
                            <div className="font-medium text-[13px] text-slate-800 leading-snug">{t.title}</div>
                          </div>
                          <div className="text-[11px] text-slate-400 mb-2">{t.project_code || t.project_name}</div>
                          <div className="flex items-center justify-between">
                            <Initials name={t.assignee_name} />
                            {due && (
                              <span className={`text-[11px] tabular-nums ${due.overdue && t.status !== "done" ? "text-red-600 font-semibold" : "text-slate-400"}`}>{due.text}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {list.length === 0 && <div className="py-8 text-center text-[12px] text-slate-300">Gol</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <Sheet open={!!panel} onOpenChange={(o) => { if (!o) setPanel(null); }}>
          <SheetContent className="w-[420px] sm:max-w-[420px] bg-white overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="font-display text-[17px]">{panel?.mode === "new" ? "Sarcină nouă" : "Editează sarcina"}</SheetTitle>
            </SheetHeader>
            <div className="space-y-4 mt-5" data-testid="task-panel">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 block mb-1">Titlu</label>
                <input data-testid="task-form-title" className="pio-input w-full" value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Ce trebuie făcut?" />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 block mb-1">Descriere</label>
                <textarea data-testid="task-form-description" rows={3} value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full text-[13px] border border-slate-200 rounded-md p-2.5 focus:border-brand focus:outline-none" />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 block mb-1">Proiect</label>
                <select data-testid="task-form-project" className="pio-input w-full" value={form.project_id}
                  onChange={e => setForm(f => ({ ...f, project_id: e.target.value }))}>
                  <option value="">Selectează proiect...</option>
                  {ctx.projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 block mb-1">Responsabil</label>
                <select data-testid="task-form-assignee" className="pio-input w-full" value={form.assignee_id}
                  onChange={e => setForm(f => ({ ...f, assignee_id: e.target.value }))}>
                  <option value="">Fără responsabil</option>
                  {ctx.employees.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 block mb-1">Termen</label>
                  <input data-testid="task-form-due" type="date" className="pio-input w-full" value={form.due_date}
                    onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} />
                </div>
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 block mb-1">Prioritate</label>
                  <select data-testid="task-form-priority" className="pio-input w-full" value={form.priority}
                    onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                    {PRIORITY.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 block mb-1">Status</label>
                <div className="flex gap-1.5">
                  {STATUS.map(s => (
                    <button key={s.value} data-testid={`task-form-status-${s.value}`}
                      onClick={() => setForm(f => ({ ...f, status: s.value }))}
                      className={`pio-badge ${form.status === s.value ? s.cls : "pio-badge-gray opacity-50"} !h-7 !px-3 cursor-pointer border-0`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                {panel?.mode === "edit" ? (
                  <button data-testid="task-delete-btn" onClick={remove}
                    className="pio-btn-ghost !text-red-600 !border-red-200 hover:!bg-red-50"><Trash2 /> Șterge</button>
                ) : <span />}
                <button data-testid="task-save-btn" onClick={save} disabled={saving} className="pio-btn">
                  {saving && <Loader2 className="animate-spin" />} {panel?.mode === "new" ? "Creează" : "Salvează"}
                </button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </PortalLayout>
  );
}
